/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { AccountOrganizationUsersTileDescription, AccountOrganizationUsersTileLinks } from './components';
import { IOrganizationUsersTileData } from './organization-users-tile.data';
import { IOrganizationUsersTileProps } from './organization-users-tile.props.autogenerated';

export interface IOrganizationUsersTileViewProps extends IOrganizationUsersTileProps<IOrganizationUsersTileData> {
    OrganizationUsersTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * OrganizationUsersTile component.
 * @extends {React.PureComponent<IOrganizationUsersTileProps<IOrganizationUsersTileData>>}
 */
class OrganizationUsersTile extends React.PureComponent<IOrganizationUsersTileProps<IOrganizationUsersTileData>> {
    private readonly telemetryContent?: ITelemetryContent;
    private readonly RetailB2BContactMultiOutletOrderingFeatureName: string =
        'Dynamics.AX.Application.RetailB2BContactMultiOutletOrderingFeature';

    constructor(props: IOrganizationUsersTileProps<IOrganizationUsersTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element | null {
        const { config, resources, data } = this.props;
        const { className, heading, links } = config;
        const { accountOrganizationUsersTileDescriptionForSingleUser, accountOrganizationUsersTileDescriptionForMultipleUsers } = resources;
        const numUsers = data.users && data.users.result;
        const isRetailB2BContactMultiOutletOrderingFeatureEnabled = data.featureState?.result?.find(
            feature => feature.Name === this.RetailB2BContactMultiOutletOrderingFeatureName
        )?.IsEnabled;
        const accountOrganizationUsersTileDescription =
            numUsers === 1 ? accountOrganizationUsersTileDescriptionForSingleUser : accountOrganizationUsersTileDescriptionForMultipleUsers;
        const customerInformation = data.customerInformation.result;

        // Organization-users tile will not be render for non-admin users.
        // Organization-users tile will not be render when RetailB2BContactMultiOutletOrderingFeature is enabled.
        if (!customerInformation?.IsB2bAdmin || isRetailB2BContactMultiOutletOrderingFeatureEnabled) {
            return null;
        }

        const viewProps = {
            ...this.props,
            className,
            OrganizationUsersTile: {
                moduleProps: this.props,
                className: classnames('ms-account-organization-users-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-organization-users-tile__heading'
                    tag={heading.tag}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <AccountOrganizationUsersTileLinks
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: (
                <AccountOrganizationUsersTileDescription description={format(accountOrganizationUsersTileDescription, numUsers || 0)} />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => {
        this.props.config.heading.text = event.target.value;
    };

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default OrganizationUsersTile;
